import { useMemo, useState, useEffect } from 'react'
import { useSelector } from '../../stores/dashboard.store'
import { Typography, Chip } from '@mui/material'
import { useMediaQuery } from '@mui/material'
import ErrorIcon from '@mui/icons-material/Error';
import RequestTable from '../../components/request-table/request-table'
import { IconButton, LinearProgress } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import useFetch, { invalidateProcessing } from '../../hooks/useFetch'
import { fetchTasks } from '../../api-client/data'
import Button from '../nura/button/button'
import RetriggerRequest from '../dialogs/retrigger-request'
import { useRunBlueprint } from '../../hooks/useRunBlueprint'
import usePolling from '../../hooks/usePolling'
import { formatDateTime } from '../../util/time'
import styles from './styles.module.css'
import ColorChip from '../color-chip';
import { stringHasContent } from '../../util/util';

const getStatusColor = (status) => {
    switch (status) {
        case "complete":
            return "green"
        case "failed":
            return "red"
        default:
            return "#d9b613"
    }
}

const isRetriggerStatus = (status) => ["complete", "failed"].includes(status)

const Sidebar = ({ setRetriggerShow, activeItem, isDev }) => {
    const { colors } = useSelector()

    const [lastServerDuration, setLastServerDuration] = useState(activeItem?.executionDurationSeconds || 0)
    const [lastServerTimestamp, setLastServerTimestamp] = useState(Date.now())
    const [tick, setTick] = useState(0)

    useEffect(() => {
        setLastServerDuration(activeItem?.executionDurationSeconds || 0)
        setLastServerTimestamp(Date.now())
    }, [activeItem?.executionDurationSeconds])

    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 1000)
        return () => clearInterval(interval)
    }, [])

    const currentNodeColor = useMemo(() => {
        if (!!activeItem?.currentNode?.outputType) return colors[activeItem?.currentNode?.outputType]

        return [0, 0, 0]
    }, [activeItem?.currentNode])

    if (activeItem?.id === undefined) return null

    return (
        <>
            <Typography className={styles.requestSidebarHeader}>Request</Typography>
            <Typography className={styles.requestSidebarTitle}>{activeItem.id}</Typography>
            <div className={styles.requestSidebarMarginTop20}>
                {isRetriggerStatus(activeItem?.status) && (
                    <Button variant="outline" onClick={() => setRetriggerShow(true)}>Trigger Request</Button>
                )}
            </div>
            <table className={styles.requestSidebarTable}>
                <tr>
                    <td className={styles.requestSidebarCellDim}><Typography>Created At</Typography></td>
                    <td><Typography className={styles.requestSidebarCellDim}>{formatDateTime(activeItem.createdAt)}</Typography></td>
                </tr>
                <tr>
                    <td className={styles.requestSidebarCellDim}><Typography>Execution Duration</Typography></td>
                    <td style={{ color: 'var(--text-primary)' }}>
                        <Typography>
                            {(() => {
                                const isFinished = isRetriggerStatus(activeItem.status)
                                const display = isFinished
                                    ? lastServerDuration
                                    : lastServerDuration + (Date.now() - lastServerTimestamp) / 1000
                                return display.toFixed(2)
                            })()}
                            <span style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}> (seconds)</span>
                        </Typography>
                    </td>
                </tr>
                <tr>
                    <td className={styles.requestSidebarCellDim}><Typography>Status</Typography></td>
                    <td>
                        <Chip
                            label={activeItem.status}
                            style={{
                                backgroundColor: getStatusColor(activeItem.status),
                                color: 'white',
                                textTransform: 'capitalize',
                            }}
                        />
                    </td>
                </tr>
                {(stringHasContent(activeItem?.currentNode?.name) && !isRetriggerStatus(activeItem?.status)) && (
                    <tr>
                        <td className={styles.requestSidebarCellDim}><Typography>Current Node</Typography></td>
                        <td>
                            <ColorChip
                                color={currentNodeColor}
                                className={styles.glow}
                                label={activeItem?.currentNode?.name}
                            />
                        </td>
                    </tr>
                )}
            </table>
            {
                activeItem?.errors?.length > 0 && (
                    <div>
                        <table>
                            <tr>
                                <td className={styles.requestSidebarCellDim} style={{ display: 'flex', gap: 5 }}>
                                    <Typography>Errors</Typography>
                                </td>
                                <td></td>
                            </tr>
                        </table>
                        <div>
                            {
                                activeItem?.errors.map(x => (
                                    <div className={styles.taskError}>
                                        <div className={styles.taskErrorTitle} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <ErrorIcon style={{ fontSize: 20, color: `var(--text-primary)` }} />
                                            {x?.title}
                                        </div>
                                        <div>{x?.description}</div>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                )
            }
            {isDev && (
                <>
                    <table className={styles.requestSidebarDivider}>
                        <tr>
                            <td className={styles.requestSidebarCellDim}><Typography>Response</Typography></td>
                            <td></td>
                        </tr>
                    </table>
                    <textarea
                        className={styles.requestSidebarResponseArea}
                        maxRows={4}
                        value={activeItem.response}
                        readOnly
                    />
                </>
            )}
        </>
    )
}

export default function DashboardRequest() {
    const projectId = useSelector(x => x?.activeProject?.id)
    const user = useSelector(x => x?.user)

    const [activeId, setActiveId] = useState()
    const [retriggerShow, setRetriggerShow] = useState(false)

    const { retrigger } = useRunBlueprint()

    const taskList = useFetch(() => fetchTasks(projectId), [], invalidateProcessing)

    useEffect(() => {
        if (activeId === undefined && taskList?.data?.length > 0) {
            setActiveId(taskList?.data[0]?.id)
        }
    }, [taskList?.data, activeId])

    const retriggerRequest = () => {
        setRetriggerShow(false)
        retrigger(activeId)
    }

    const activeItem = useMemo(() => {
        if (activeId === undefined) return taskList?.data[0]

        return taskList?.data?.find(x => x?.id === activeId)
    }, [activeId, taskList])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const tidParam = params.get('tid')
        if (tidParam) {
            setActiveId(tidParam)
        }
    }, [])

    usePolling(taskList.refresh, 2000, true)

    const isMobile = useMediaQuery('(max-width: 900px)')
    const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false)

    const handleRowClick = (id) => {
        setActiveId(id)
        if (isMobile) setMobileSidebarVisible(true)
    }

    return (
        <>
            {isMobile && (
                <div className={styles.actionGroup} style={{ position: 'absolute', top: 0, right: 40, zIndex: 1 }}>
                    <div className={styles.innerButtonBar}>
                        <IconButton style={{ color: 'var(--text-primary)' }} onClick={taskList.refresh}>
                            <RefreshIcon />
                        </IconButton>
                    </div>
                </div>)}

            {isMobile && mobileSidebarVisible && (
                <div styles={styles.actionGroup} style={{ position: 'absolute', bottom: 100, right: 40, zIndex: 1 }}>
                    <div className={styles.innerButtonBar}>
                        <IconButton style={{ color: 'var(--text-primary)' }} onClick={() => setMobileSidebarVisible(false)}>
                            <ArrowBackIcon />
                        </IconButton>
                    </div>
                </div>
            )}

            <RetriggerRequest show={retriggerShow} setShow={setRetriggerShow} onTrigger={retriggerRequest} />

            <div className={styles.dashboardHeader}>
                <div className={styles.dashboardTitle}>
                    <Typography variant="h6" className={styles.requestTitleText}>Request</Typography>
                </div>
                {!isMobile && (
                    <IconButton style={{ color: 'var(--text-primary)' }} onClick={taskList.refresh}>
                        <RefreshIcon style={{ fontSize: 20 }} />
                    </IconButton>
                )}
            </div>


            <div className={styles.requestContent}>
                {!isMobile || !mobileSidebarVisible ? (
                    <div className={styles.requestTable}>
                        <RequestTable
                            activeId={activeItem?.id}
                            data={taskList?.data || []}
                            onClick={handleRowClick}
                        />
                    </div>
                ) : null}

                {(!isMobile || mobileSidebarVisible) && (
                    <div className={styles.requestMain}>

                        {activeItem?.status === "processing" && (
                            <div className={styles.requestProgress}>
                                <LinearProgress
                                    variant="determinate"
                                    value={Number(activeItem?.response) * 100}
                                />
                            </div>
                        )}
                        <div key={activeItem?.status} className={styles.requestSidebar}>
                            {taskList?.data?.length > 0 && (
                                <Sidebar isDev={user?.at === "dev"} activeItem={activeItem} setRetriggerShow={() => setRetriggerShow(true)} />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}
